/**
 * Fonte única das seções do módulo RAIZ — consumida pelas tabs do RaizLayout.
 * No app standalone o módulo vive na raiz do site (era /management/raiz no Aura).
 */
/**
 * Grupo de navegação — o menu "Mais" (desktop) e o sheet (celular) listam as
 * seções por grupo, com cabeçalho. Sem isso eram 10 itens planos misturando
 * "Perfis de alarme" com "Bancada" e "Sobre".
 */
export type RaizGroup = 'operacao' | 'conformidade' | 'administracao' | 'plataforma'

export const RAIZ_GROUP_LABEL: Record<RaizGroup, string> = {
  operacao: 'Operação',
  conformidade: 'Conformidade',
  administracao: 'Administração',
  plataforma: 'Plataforma',
}

/** Ordem de exibição dos grupos — do dia a dia do operador ao interno da plataforma. */
export const RAIZ_GROUP_ORDER: RaizGroup[] = ['operacao', 'conformidade', 'administracao', 'plataforma']

export interface RaizSection {
  path: string
  label: string
  group: RaizGroup
  /** Só aparece para admin (ex.: Gestão de Devices, que manipula segredos). */
  adminOnly?: boolean
  /** Só para a PLATAFORMA (F4): visão/ações sobre todas as empresas. */
  platformOnly?: boolean
  /**
   * Seção primária: aparece sempre como aba no topo. As não-primárias vão para o
   * menu "Mais" (overflow), reduzindo as escolhas visíveis sem esconder função.
   */
  primary?: boolean
  /**
   * Fora da navegação (abas, "Mais", bottom nav): alcançada por outro caminho —
   * "Minha conta" vive no menu do avatar. Continua sendo seção para o breadcrumb
   * e para a paleta de comandos (#131).
   */
  hidden?: boolean
}

export const RAIZ_SECTIONS: RaizSection[] = [
  { path: '/', label: 'Visão geral', group: 'operacao', primary: true },
  // Hub com sub-rotas (/events/excursions e /events/warnings) — ver RaizEvents.
  { path: '/events', label: 'Eventos', group: 'operacao', primary: true },
  { path: '/map', label: 'Mapa', group: 'operacao', primary: true },
  { path: '/units', label: 'Unidades', group: 'operacao', primary: true },
  { path: '/compliance', label: 'Conformidade', group: 'conformidade', primary: true },
  { path: '/report', label: 'Relatórios', group: 'conformidade', primary: true },
  // Perfis de alarme definem o CRITÉRIO de julgamento — é conformidade, não ajuste de sistema.
  { path: '/alarm-profiles', label: 'Perfis de alarme', group: 'conformidade' },
  { path: '/gap', label: 'GAP normativo', group: 'conformidade' },
  { path: '/device-keys', label: 'Gestão de Devices', group: 'administracao', adminOnly: true },
  { path: '/firmware', label: 'Firmware', group: 'administracao', adminOnly: true },
  // Hub com sub-rotas (/settings/<seção>) — ver settings/settingsSections.ts.
  { path: '/settings', label: 'Configurações', group: 'administracao', adminOnly: true },
  { path: '/bench', label: 'Bancada', group: 'plataforma', platformOnly: true },
  // Lab de Bateria: cruza empresas, por isso é de plataforma (spec 2026-10-21).
  { path: '/battery', label: 'Bateria', group: 'plataforma', platformOnly: true },
  { path: '/companies', label: 'Empresas', group: 'plataforma', platformOnly: true },
  // Sobre e Minha conta vivem no menu do avatar (e no fim do sheet do celular):
  // não são lugares de trabalho, não disputam espaço com as seções.
  { path: '/about', label: 'Sobre o RAIZ', group: 'operacao', hidden: true },
  { path: '/account', label: 'Minha conta', group: 'operacao', hidden: true },
]

/** Visibilidade de seção por papel — mesma regra nas tabs (desktop) e na bottom nav (mobile). */
export const sectionVisible = (
  s: Pick<RaizSection, 'adminOnly' | 'platformOnly'>,
  viewer: { isAdmin: boolean, isPlatformViewer: boolean }
): boolean => (!s.adminOnly || viewer.isAdmin) && (!s.platformOnly || viewer.isPlatformViewer)

/**
 * A rota atual pertence à seção? Igualdade ou sub-rota (`/settings/users` é
 * Configurações). A raiz só casa por igualdade — senão seria pai de tudo.
 */
export const pathInSection = (sectionPath: string, pathname: string): boolean =>
  pathname === sectionPath || (sectionPath !== '/' && pathname.startsWith(`${sectionPath}/`))

/** Seção dona da rota atual (ou undefined: detalhe do device, rota desconhecida). */
export const sectionForPath = (pathname: string, sections: RaizSection[] = RAIZ_SECTIONS): RaizSection | undefined =>
  sections.find((s) => pathInSection(s.path, pathname))

/** Seções que entram na navegação (abas, "Mais", bottom nav) — as hidden ficam de fora. */
export const RAIZ_NAV_SECTIONS = RAIZ_SECTIONS.filter((s) => !s.hidden)

/** Seções sempre visíveis como abas no topo. */
export const RAIZ_PRIMARY_SECTIONS = RAIZ_NAV_SECTIONS.filter((s) => s.primary)

/** Seções que ficam no menu "Mais" (overflow) — respeitam adminOnly no consumo. */
export const RAIZ_SECONDARY_SECTIONS = RAIZ_NAV_SECTIONS.filter((s) => !s.primary)

/** Agrupa seções (já filtradas por papel) na ordem dos grupos; grupos vazios somem. */
export function groupSections<T extends Pick<RaizSection, 'group'>> (sections: T[]): Array<{ group: RaizGroup, label: string, sections: T[] }> {
  return RAIZ_GROUP_ORDER
    .map((group) => ({ group, label: RAIZ_GROUP_LABEL[group], sections: sections.filter((s) => s.group === group) }))
    .filter((g) => g.sections.length > 0)
}

/** Largura única do módulo — telas densas e de leitura compartilham, sem "pulo" ao navegar. */
export const RAIZ_MAXWIDTH = 1400
